'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { DollarSign, TrendingUp, AlertCircle } from 'lucide-react'

export default function RateCalculator() {
  const [hospitalRate, setHospitalRate] = useState(100)
  const fixedMargin = 8
  const nurseRate = hospitalRate - fixedMargin

  const traditionalAgencyMargin = hospitalRate * 0.25
  const traditionalNurseRate = hospitalRate - traditionalAgencyMargin

  const savings = nurseRate - traditionalNurseRate

  return (
    <section id="pricing" className="py-20 bg-gradient-to-b from-gray-900 to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            <span className="text-gradient bg-gradient-to-r from-accent-400 to-primary-400">
              Radical Transparency
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            See exactly how much you earn. No hidden fees. No surprises.
            <br />
            Just a simple, fixed $8/hour margin.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Interactive Calculator */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass-effect rounded-3xl p-8 space-y-6"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 bg-accent-500/20 rounded-xl">
                <DollarSign className="w-6 h-6 text-accent-400" />
              </div>
              <h3 className="text-2xl font-bold text-white">Live Rate Calculator</h3>
            </div>

            {/* Slider */}
            <div className="space-y-4">
              <label className="text-gray-300 font-semibold">
                Hospital Pays (per hour)
              </label>
              <div className="relative">
                <input
                  type="range"
                  min="50"
                  max="200"
                  value={hospitalRate}
                  onChange={(e) => setHospitalRate(Number(e.target.value))}
                  className="w-full h-3 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-accent-500"
                  style={{
                    background: `linear-gradient(to right, #14b8a6 0%, #14b8a6 ${((hospitalRate - 50) / 150) * 100}%, #374151 ${((hospitalRate - 50) / 150) * 100}%, #374151 100%)`,
                  }}
                />
                <motion.div
                  className="absolute -top-12 left-1/2 transform -translate-x-1/2 px-4 py-2 bg-accent-500 rounded-lg text-white font-bold"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 0.3 }}
                  key={hospitalRate}
                >
                  ${hospitalRate}/hr
                </motion.div>
              </div>
            </div>

            {/* Breakdown */}
            <div className="space-y-4 pt-6 border-t border-gray-700">
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Hospital Pays</span>
                <span className="text-2xl font-bold text-white">${hospitalRate}/hr</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Velocity Margin (Fixed)</span>
                <span className="text-xl font-semibold text-primary-400">-${fixedMargin}/hr</span>
              </div>
              <div className="h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent" />
              <div className="flex justify-between items-center">
                <span className="text-gray-200 font-semibold">You Take Home</span>
                <motion.span
                  className="text-3xl font-bold text-gradient bg-gradient-to-r from-accent-400 to-primary-400"
                  key={nurseRate}
                  initial={{ scale: 1.2 }}
                  animate={{ scale: 1 }}
                >
                  ${nurseRate}/hr
                </motion.span>
              </div>
            </div>

            {/* Annual projection */}
            <div className="mt-6 p-4 bg-accent-500/10 border border-accent-500/30 rounded-xl">
              <div className="text-sm text-gray-300 mb-1">Annual Projection (2,080 hours)</div>
              <div className="text-2xl font-bold text-accent-400">
                ${(nurseRate * 2080).toLocaleString()}/year
              </div>
            </div>
          </motion.div>

          {/* Comparison */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="glass-effect rounded-3xl p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-3 bg-red-500/20 rounded-xl">
                  <AlertCircle className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-2xl font-bold text-white">Traditional Agencies</h3>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Hospital Pays</span>
                  <span className="text-xl font-semibold text-white">${hospitalRate}/hr</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Agency Takes (~25%)</span>
                  <span className="text-xl font-semibold text-red-400">-${traditionalAgencyMargin.toFixed(2)}/hr</span>
                </div>
                <div className="h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent" />
                <div className="flex justify-between items-center">
                  <span className="text-gray-200 font-semibold">You Take Home</span>
                  <span className="text-2xl font-bold text-gray-400">${traditionalNurseRate.toFixed(2)}/hr</span>
                </div>
              </div>
            </div>

            {/* Savings highlight */}
            <motion.div
              className="glass-effect rounded-3xl p-8 bg-gradient-to-br from-accent-500/20 to-primary-500/20 border-2 border-accent-500/50"
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center space-x-3 mb-4">
                <TrendingUp className="w-8 h-8 text-accent-400" />
                <h3 className="text-xl font-bold text-white">Your Advantage</h3>
              </div>
              <div className="space-y-2">
                <div className="text-5xl font-bold text-gradient bg-gradient-to-r from-accent-400 to-primary-400">
                  +${savings.toFixed(2)}/hr
                </div>
                <div className="text-gray-300">More in your pocket</div>
                <div className="text-sm text-gray-400 mt-4">
                  That&apos;s <span className="text-accent-400 font-bold">${(savings * 2080).toLocaleString()}</span> more per year!
                </div>
              </div>
            </motion.div>

            {/* Trust badge */}
            <div className="text-center p-6 glass-effect rounded-2xl">
              <div className="text-sm text-gray-400 mb-2">Guaranteed Fixed Margin</div>
              <div className="text-2xl font-bold text-white">Always $8/hour</div>
              <div className="text-xs text-gray-500 mt-2">No hidden fees • No surprises • Ever.</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
